/**
 * Shared collection dashboard — read-only stats view for a shared user's collection
 */

import { useParams, NavLink } from 'react-router-dom';
import { StatsCard, ChartCard } from '../components/dashboard';
import { useSharedWithMe } from '../hooks/useSharing';
import {
  useSharedDashboardStats,
  useSharedBottlesByType,
  useSharedBottlesByCountry,
  useSharedRecentBottles
} from '../hooks/useSharedDashboard';
import { formatCurrency, formatDate } from '../utils/formatters';
import './SharedCollection.css';
import './Dashboard.css';
import './SharedDashboard.css';

export default function SharedDashboard() {
  const { shareId } = useParams();

  const { data: sharedList = [] } = useSharedWithMe();
  const share = sharedList.find(s => s.share_id === shareId);
  const userId = share?.owner_id;
  const ownerEmail = share?.owner_email || 'Shared User';
  const ownerLabel = share?.owner_display_name || ownerEmail;
  const canViewBottles = share?.share_bottles !== false;
  const canViewHunt = share?.share_hunt !== false;

  const { data: stats, isLoading: statsLoading } = useSharedDashboardStats(userId);
  const { data: bottlesByType } = useSharedBottlesByType(userId);
  const { data: bottlesByCountry } = useSharedBottlesByCountry(userId);
  const { data: recentBottles, isLoading: recentLoading } = useSharedRecentBottles(userId, 5);

  const statusBreakdown = [
    { label: 'Unopened', value: stats?.unopened_count || 0 },
    { label: 'Opened', value: stats?.opened_count || 0 },
    { label: 'Empty', value: stats?.empty_count || 0 }
  ];

  const typeChartData = (bottlesByType || []).map(item => ({
    label: item.type,
    value: Number(item.count)
  }));

  const countryChartData = (bottlesByCountry || []).map(item => ({
    label: item.country,
    value: Number(item.count)
  }));

  return (
    <div className="shared-page">
      <div className="shared-page__header">
        <p className="shared-page__viewing-label">
          Viewing <strong title={share?.owner_display_name ? ownerEmail : undefined}>{ownerLabel}</strong>&rsquo;s collection
        </p>
        <div className="shared-page__nav-tabs">
          <NavLink
            to={`/shared/${shareId}/dashboard`}
            className={({ isActive }) => `shared-page__nav-tab ${isActive ? 'shared-page__nav-tab--active' : ''}`}
          >
            Dashboard
          </NavLink>
          {canViewBottles && (
            <NavLink
              to={`/shared/${shareId}/bottles`}
              className={({ isActive }) => `shared-page__nav-tab ${isActive ? 'shared-page__nav-tab--active' : ''}`}
            >
              Bottles
            </NavLink>
          )}
          {canViewHunt && (
            <NavLink
              to={`/shared/${shareId}/hunt`}
              className={({ isActive }) => `shared-page__nav-tab ${isActive ? 'shared-page__nav-tab--active' : ''}`}
            >
              Hunt List
            </NavLink>
          )}
        </div>
      </div>

      {!canViewBottles ? (
        <p className="shared-page__locked">The owner has not shared their bottle collection with you.</p>
      ) : (
        <div className="dashboard">
          <div className="dashboard__stats">
            <StatsCard
              title="Total Bottles"
              value={statsLoading ? '-' : stats?.total_bottles || 0}
              breakdown={statusBreakdown}
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 2h4" />
                  <path d="M10 2v4l-2 2v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V8l-2-2V2" />
                  <path d="M8 14h8" />
                </svg>
              }
            />

            <StatsCard
              title="Total Distilleries"
              value={statsLoading ? '-' : stats?.total_distilleries || 0}
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 21h18" />
                  <path d="M5 21V7l8-4v18" />
                  <path d="M19 21V11l-6-4" />
                </svg>
              }
            />

            <StatsCard
              title="Collection Value"
              value={statsLoading ? '-' : formatCurrency(stats?.total_value || 0)}
              subtitle="Total purchase price"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              }
            />
          </div>

          <div className="dashboard__charts">
            <ChartCard
              title="Bottles by Type"
              data={typeChartData}
              emptyMessage="No bottles in this collection yet"
            />
            <ChartCard
              title="Bottles by Country"
              data={countryChartData}
              emptyMessage="No bottles with distilleries yet"
            />
          </div>

          <div className="dashboard__activity">
            <div className="shared-recent">
              <h3 className="shared-recent__title">Recent Additions</h3>
              {recentLoading ? (
                <div className="shared-recent__loading">Loading...</div>
              ) : !recentBottles?.length ? (
                <div className="shared-recent__empty">No bottles added yet</div>
              ) : (
                <ul className="shared-recent__list">
                  {recentBottles.map(bottle => (
                    <li key={bottle.id} className="shared-recent__item">
                      <div className="shared-recent__info">
                        <span className="shared-recent__name">{bottle.bottle_name}</span>
                        <span className="shared-recent__type">
                          {bottle.type}
                          {bottle.distilleries?.distillery_name && ` \u2013 ${bottle.distilleries.distillery_name}`}
                        </span>
                      </div>
                      <div className="shared-recent__meta">
                        <span className={`shared-recent__status shared-recent__status--${bottle.status.toLowerCase()}`}>
                          {bottle.status}
                        </span>
                        <span className="shared-recent__date">
                          {bottle.purchase_date ? formatDate(bottle.purchase_date) : 'No date'}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
